/**
 * useAccountingUtils Hook
 * 정산 관련 유틸리티 함수들 - 메모이제이션 적용
 * 순수 함수는 accountingLabelUtils.ts로 분리됨
 */
import { useCallback } from "react";
import { formatCurrency } from "@/shared/utils/formatter";
import { STORAGE_KEYS } from '@/shared/constants/storageKeys';
import {
    getPartnerStatusBadge as _getPartnerStatusBadge,
    getRiderContractStatusBadge as _getRiderContractStatusBadge,
    getStatusBadge as _getStatusBadge,
    getGradeBadge as _getGradeBadge,
    getGradeBadgeClass as _getGradeBadgeClass,
    getStatusBadgeClass as _getStatusBadgeClass,
    getRiderContractStatusBadgeClass as _getRiderContractStatusBadgeClass,
    getPreAccountBadgeClass as _getPreAccountBadgeClass,
    getPreAccountBadgeText as _getPreAccountBadgeText,
    getPartnerRowClassName as _getPartnerRowClassName,
    getRiderRowClassName as _getRiderRowClassName,
    isPartnerAccountDisabled as _isPartnerAccountDisabled,
    isRiderAccountDisabled as _isRiderAccountDisabled
} from '@/shared/utils/accountingLabelHelpers';

export const useAccountingUtils = () => {
    // 협력사 상태 배지 생성
    const getPartnerStatusBadge = useCallback(_getPartnerStatusBadge, []);

    // 라이더 계약상태 배지 생성
    const getRiderContractStatusBadge = useCallback(_getRiderContractStatusBadge, []);

    // 상태 배지 생성 (통합)
    const getStatusBadge = useCallback(_getStatusBadge, []);

    // 등급 배지 생성
    const getGradeBadge = useCallback(_getGradeBadge, []);

    // 숫자 포맷팅 (천 단위 콤마)
    const formatNumber = useCallback((num: number | null | undefined): string => {
        if (num === null || num === undefined) return '-';
        return formatCurrency(num);
    }, []);

    // 등급별 badge CSS 클래스 반환
    const getGradeBadgeClass = useCallback(_getGradeBadgeClass, []);

    // 일차감 버튼 클릭
    const handleSendMoney = useCallback((
        partnerName: string,
        amount: string,
        suppId?: string,
        riderId?: string
    ) => {
        if (suppId) {
            const prefillPayload = {
                suppId,
                riderId,
                deductTarget: riderId ? '라이더' : '협력사',
                partnerName,
                amount,
            };
            try {
                sessionStorage.setItem(STORAGE_KEYS.PARTNER_DEDUCTION_PREFILL, JSON.stringify(prefillPayload));
            } catch {
                // sessionStorage 저장 실패는 무시
            }
        }

        // 대시보드에 일차감 내역 페이지로 이동 이벤트 전달
        const navigateEvent = new CustomEvent('dashboard:navigate', {
            detail: { page: 'partner_deduction_history' },
        });
        window.dispatchEvent(navigateEvent);
    }, []);

    // 상태 배지 CSS 클래스 반환
    const getStatusBadgeClass = useCallback(_getStatusBadgeClass, []);

    // 협력사 테이블 Row 클래스명 결정
    const getPartnerRowClassName = useCallback(_getPartnerRowClassName, []);

    // 협력사 계좌 상태 확인
    const isPartnerAccountDisabled = useCallback(_isPartnerAccountDisabled, []);

    // 라이더 계약상태 배지 CSS 클래스 반환
    const getRiderContractStatusBadgeClass = useCallback(_getRiderContractStatusBadgeClass, []);

    // 선정산 배지 CSS 클래스 반환
    const getPreAccountBadgeClass = useCallback(_getPreAccountBadgeClass, []);

    // 선정산 배지 텍스트 반환
    const getPreAccountBadgeText = useCallback(_getPreAccountBadgeText, []);

    // 라이더 테이블 Row 클래스명 결정
    const getRiderRowClassName = useCallback(_getRiderRowClassName, []);

    // 라이더 계좌 상태 확인
    const isRiderAccountDisabled = useCallback(_isRiderAccountDisabled, []);

    return {
        getPartnerStatusBadge,
        getRiderContractStatusBadge,
        getStatusBadge,
        getGradeBadge,
        getGradeBadgeClass,
        formatNumber,
        handleSendMoney,
        getStatusBadgeClass,
        getPartnerRowClassName,
        isPartnerAccountDisabled,
        getRiderContractStatusBadgeClass,
        getPreAccountBadgeClass,
        getPreAccountBadgeText,
        getRiderRowClassName,
        isRiderAccountDisabled
    };
};
